<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';

/**
 * Toast d'invitation à installer la PWA. Capture le `beforeinstallprompt`
 * du navigateur (Chrome/Edge/Brave/Android) et propose l'install au
 * joueur. Sur iOS le prompt natif n'existe pas — on s'appuie sur
 * "Ajouter à l'écran d'accueil" via Safari (instructions dans la page
 * dédiée si on en ajoute une un jour).
 *
 * Si l'utilisateur dismiss, on attend 24h avant de réafficher la
 * proposition pour ne pas être intrusif.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const STORAGE_DISMISSED = 'theho.pwa.install.dismissed';
const STORAGE_ACCEPTED = 'theho.pwa.install.accepted';
const DISMISS_COOLDOWN_MS = 24 * 60 * 60 * 1000;

const showPrompt = ref(false);
let deferredPrompt: BeforeInstallPromptEvent | null = null;

function handleBeforeInstall(e: Event): void {
  e.preventDefault();
  deferredPrompt = e as BeforeInstallPromptEvent;
  const dismissed = localStorage.getItem(STORAGE_DISMISSED);
  if (dismissed && Date.now() - Number(dismissed) < DISMISS_COOLDOWN_MS) return;
  showPrompt.value = true;
}

async function install(): Promise<void> {
  if (!deferredPrompt) return;
  await deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  deferredPrompt = null;
  showPrompt.value = false;
  if (outcome === 'accepted') {
    localStorage.setItem(STORAGE_ACCEPTED, 'true');
  }
}

function dismiss(): void {
  showPrompt.value = false;
  localStorage.setItem(STORAGE_DISMISSED, String(Date.now()));
}

function handleAppInstalled(): void {
  showPrompt.value = false;
  deferredPrompt = null;
  localStorage.setItem(STORAGE_ACCEPTED, 'true');
}

onMounted(() => {
  window.addEventListener('beforeinstallprompt', handleBeforeInstall);
  window.addEventListener('appinstalled', handleAppInstalled);
});

onUnmounted(() => {
  window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  window.removeEventListener('appinstalled', handleAppInstalled);
});
</script>

<template>
  <Transition name="slide-up">
    <div v-if="showPrompt" class="install-prompt" role="dialog" aria-live="polite">
      <div class="install-prompt__header">
        <img src="/pwa-192x192.png" alt="The H'O" class="install-prompt__logo" />
        <h3 class="install-prompt__title">Installer The H'O</h3>
        <button class="install-prompt__close" @click="dismiss" aria-label="Fermer">&times;</button>
      </div>
      <p class="install-prompt__message">
        Installe l'app pour un accès rapide depuis ton écran d'accueil et un
        mode hors-ligne partiel.
      </p>
      <div class="install-prompt__actions">
        <button class="install-btn install-btn--later" @click="dismiss">Plus tard</button>
        <button class="install-btn install-btn--install" @click="install">Installer</button>
      </div>
    </div>
  </Transition>
</template>

<style lang="scss" scoped>
.install-prompt {
  position: fixed;
  bottom: calc(80px + env(safe-area-inset-bottom, 0px));
  left: $space-3;
  right: $space-3;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: $space-2;
  padding: $space-3 $space-4;
  max-width: 400px;
  margin: 0 auto;
  background: $bg-elevated;
  border: 1px solid rgba(125, 211, 252, 0.3);
  border-radius: $radius-md;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(12px);

  @media (min-width: 768px) {
    bottom: $space-4;
    left: auto;
    right: $space-4;
    width: 360px;
    max-width: calc(100vw - #{$space-4 * 2});
  }
}

.install-prompt__header {
  display: flex;
  align-items: center;
  gap: $space-2;
}

.install-prompt__logo {
  width: 40px;
  height: 40px;
  border-radius: $radius-sm;
  flex-shrink: 0;
}

.install-prompt__title {
  flex: 1;
  margin: 0;
  font-size: $font-size-md;
  font-weight: $font-weight-semibold;
  color: $text-primary;
}

.install-prompt__close {
  background: none;
  border: none;
  font-size: 20px;
  line-height: 1;
  color: $text-muted;
  cursor: pointer;
  padding: 0 4px;
  flex-shrink: 0;

  &:hover { color: $text-primary; }
}

.install-prompt__message {
  margin: 0;
  font-size: $font-size-sm;
  color: $text-secondary;
  line-height: 1.5;
}

.install-prompt__actions {
  display: flex;
  justify-content: flex-end;
  gap: $space-2;
}

.install-btn {
  padding: 6px 14px;
  border-radius: $radius-sm;
  border: 1px solid transparent;
  font-size: $font-size-sm;
  font-weight: $font-weight-medium;
  cursor: pointer;
  transition: all $duration-fast $easing-default;
}

.install-btn--later {
  background: transparent;
  color: $text-secondary;
  border-color: $border-subtle;

  &:hover {
    background: $bg-tertiary;
    color: $text-primary;
  }
}

.install-btn--install {
  background: $color-accent-500;
  color: $text-primary;

  &:hover {
    background: $color-accent-600;
    box-shadow: 0 4px 16px rgba(59, 130, 246, 0.35);
  }
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.35s $easing-default;
}
.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(20px);
  opacity: 0;
}
</style>
